import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState, useMemo } from 'react';
import { Box, Grid, Text } from '@chakra-ui/react';
import { styles } from './organization.styles';
import Input from '../../components/input/input';
import { InputSize } from '../../core/enums';
import { Checkbox, CheckboxSize } from '../../components/checkbox/checkbox';
import { states } from './organization-constants';
import { SubHeader } from './subheader';
import STUB_ORG, { STUB_ORG_RELATIONSHIP } from './stub-organization';
import { Select } from '../../components/select/select';
import { convertTimeZones } from './organization-utils';
import { ServiceLines } from './service-line-selection';
const initialOrgForm = {
    customTenantId: true,
    edits: [],
    organization: STUB_ORG,
    orgRelationships: [
        {
            type: 'ADD',
            organizationRelationship: STUB_ORG_RELATIONSHIP
        }
    ]
};
const hasError = (key, errors)=>{
    return errors && key in errors;
};
const getInlineError = (errors)=>{
    return errors.join(' ');
};
const displayErrorIfPresent = (key, errors)=>{
    return hasError(key, errors) ? getInlineError(errors[key]) : '';
};
const OrganizationForm = (props)=>{
    const classes = styles();
    const { title, organization, relationship, errors, edit, timezones, viewOnly, serviceLines, showServiceLines, orgRelationships } = props;
    const [organizationDetails, setOrganizationDetails] = useState(initialOrgForm);
    const orgState = useMemo(()=>{
        return {
            ...STUB_ORG,
            ...organization
        };
    }, [
        organization
    ]);
    useEffect(()=>{
        setOrganizationDetails({
            organization: orgState,
            edits: [],
            orgRelationships: [
                {
                    type: 'ADD',
                    organizationRelationship: {
                        ...relationship
                    }
                }
            ],
            customTenantId: true
        });
    }, [
        relationship,
        orgState
    ]);
    const handleInputChange = (event)=>{
        const target = event.target;
        const { value, name } = target;
        setOrganizationDetails((prevState)=>({
                ...prevState,
                organization: {
                    ...prevState.organization,
                    [name]: value
                }
            }));
    };
    const toggleCustomTenantId = ()=>{
        setOrganizationDetails((prevState)=>({
                ...prevState,
                customTenantId: !prevState.customTenantId
            }));
    };
    const toggleActiveOrganization = ()=>{
        setOrganizationDetails((prevState)=>({
                ...prevState,
                organization: {
                    ...prevState.organization,
                    active: !prevState.organization.active
                }
            }));
    };
    const toggleAWSCognitoIndicator = ()=>{
        setOrganizationDetails((prevState)=>({
                ...prevState,
                organization: {
                    ...prevState.organization,
                    awsCognitoIndicator: !prevState.organization.awsCognitoIndicator
                }
            }));
    };
    const toggleSSOIndicator = ()=>{
        setOrganizationDetails((prevState)=>({
                ...prevState,
                organization: {
                    ...prevState.organization,
                    ssoIndicator: !prevState.organization.ssoIndicator
                }
            }));
    };
    const handleSubmit = ()=>{
        props.doSave(organizationDetails.organization, organizationDetails.edits);
    };
    const handleEditToggle = ()=>{
        props.cancel();
    };
    const handleServiceLine = (edits)=>{
        setOrganizationDetails((prevState)=>({
                ...prevState,
                edits
            }));
    };
    return /*#__PURE__*/ _jsxs(Box, {
        children: [
            /*#__PURE__*/ _jsx(SubHeader, {
                linkButtonName: 'Cancel',
                actionName: 'Submit',
                title: title,
                onLinkButtonClick: handleEditToggle,
                onActionClick: handleSubmit,
                isActionDisabled: viewOnly,
                className: classes.text
            }),
            /*#__PURE__*/ _jsxs(Grid, {
                sx: classes.fieldsets,
                children: [
                    /*#__PURE__*/ _jsxs("fieldset", {
                        style: classes.fieldset,
                        children: [
                            /*#__PURE__*/ _jsx("legend", {
                                style: classes.legend,
                                children: "Info"
                            }),
                            /*#__PURE__*/ _jsxs(Grid, {
                                sx: classes.grid,
                                children: [
                                    /*#__PURE__*/ _jsx(Input, {
                                        placeholder: 'Enter name',
                                        label: 'Name',
                                        isRequired: true,
                                        size: InputSize.MD,
                                        onChange: handleInputChange,
                                        name: "organizationName",
                                        textError: displayErrorIfPresent('organizationName', errors),
                                        isError: hasError('organizationName', errors),
                                        value: organizationDetails?.organization?.organizationName,
                                        dataTestId: "organizationName"
                                    }),
                                    /*#__PURE__*/ _jsx(Input, {
                                        placeholder: 'Enter display Name',
                                        label: 'Display Name',
                                        size: InputSize.MD,
                                        classes: {
                                            root: classes.input
                                        },
                                        onChange: handleInputChange,
                                        name: "displayName",
                                        textError: displayErrorIfPresent('displayName', errors),
                                        isError: hasError('displayName', errors),
                                        value: organizationDetails.organization.displayName ?? '',
                                        dataTestId: "displayName"
                                    }),
                                    /*#__PURE__*/ _jsx(Input, {
                                        placeholder: 'Enter short display name',
                                        label: 'Short Display Name',
                                        size: InputSize.MD,
                                        classes: {
                                            root: classes.input
                                        },
                                        onChange: handleInputChange,
                                        name: "shortDisplayName",
                                        textError: displayErrorIfPresent('shortDisplayName', errors),
                                        isError: hasError('shortDisplayName', errors),
                                        value: organizationDetails.organization.shortDisplayName ?? '',
                                        dataTestId: "shortDisplayName"
                                    }),
                                    /*#__PURE__*/ _jsx(Input, {
                                        label: 'Code',
                                        placeholder: 'Enter code',
                                        isRequired: true,
                                        size: InputSize.MD,
                                        classes: {
                                            root: classes.input
                                        },
                                        onChange: handleInputChange,
                                        name: "organizationCode",
                                        textError: displayErrorIfPresent('organizationCode', errors),
                                        isError: hasError('organizationCode', errors),
                                        value: organizationDetails?.organization?.organizationCode,
                                        dataTestId: "organizationCode"
                                    }),
                                    !edit && /*#__PURE__*/ _jsx(Grid, {
                                        sx: classes.checkbox,
                                        children: /*#__PURE__*/ _jsx(Checkbox, {
                                            label: 'Generate a Tenant ID for me',
                                            size: CheckboxSize.MD,
                                            isChecked: !organizationDetails?.customTenantId,
                                            onChange: toggleCustomTenantId,
                                            name: "tenantCheckbox",
                                            dataTestId: "tenantId"
                                        })
                                    }),
                                    (organizationDetails.customTenantId || edit) && /*#__PURE__*/ _jsx(Input, {
                                        label: 'Tenant ID',
                                        size: InputSize.MD,
                                        name: "tenantId",
                                        textError: displayErrorIfPresent('tenantId', errors),
                                        isError: hasError('tenantId', errors),
                                        value: organizationDetails?.organization?.tenantId,
                                        isDisabled: edit || viewOnly,
                                        classes: {
                                            root: edit ? classes.input : classes.checkbox
                                        },
                                        dataTestId: "tenantId-input"
                                    }),
                                    showServiceLines && /*#__PURE__*/ _jsx(ServiceLines, {
                                        serviceLines: serviceLines,
                                        relationship: relationship,
                                        orgRelationships: orgRelationships,
                                        onChange: handleServiceLine
                                    }),
                                    /*#__PURE__*/ _jsx(Grid, {
                                        sx: classes.checkbox,
                                        children: /*#__PURE__*/ _jsx(Checkbox, {
                                            label: 'Active',
                                            size: CheckboxSize.MD,
                                            isChecked: organizationDetails?.organization?.active,
                                            onChange: toggleActiveOrganization,
                                            name: "activeCheckbox",
                                            dataTestId: "tenantId"
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    !showServiceLines && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs("fieldset", {
                                style: classes.fieldset,
                                children: [
                                    /*#__PURE__*/ _jsx("legend", {
                                        style: classes.legend,
                                        children: "Address"
                                    }),
                                    /*#__PURE__*/ _jsxs(Grid, {
                                        sx: classes.grid,
                                        children: [
                                            /*#__PURE__*/ _jsx(Input, {
                                                label: 'Address Line 1',
                                                placeholder: 'Enter address line 1',
                                                size: InputSize.MD,
                                                onChange: handleInputChange,
                                                name: "address1",
                                                textError: displayErrorIfPresent('address1', errors),
                                                isError: hasError('address1', errors),
                                                value: organizationDetails?.organization?.address1 ?? '',
                                                dataTestId: "address1"
                                            }),
                                            /*#__PURE__*/ _jsx(Input, {
                                                label: 'Address Line 2',
                                                placeholder: 'Enter address line 2',
                                                size: InputSize.MD,
                                                onChange: handleInputChange,
                                                name: "address2",
                                                classes: {
                                                    root: classes.input
                                                },
                                                textError: displayErrorIfPresent('address2', errors),
                                                isError: hasError('address2', errors),
                                                value: organizationDetails?.organization?.address2 ?? '',
                                                dataTestId: "address2"
                                            }),
                                            /*#__PURE__*/ _jsxs(Grid, {
                                                templateColumns: 'repeat(2, 1fr)',
                                                columnGap: 3,
                                                children: [
                                                    /*#__PURE__*/ _jsx(Input, {
                                                        placeholder: 'Enter city',
                                                        label: 'City',
                                                        size: InputSize.MD,
                                                        onChange: handleInputChange,
                                                        name: "city",
                                                        textError: displayErrorIfPresent('city', errors),
                                                        isError: hasError('city', errors),
                                                        classes: {
                                                            root: classes.input
                                                        },
                                                        value: organizationDetails?.organization?.city ?? '',
                                                        dataTestId: "city"
                                                    }),
                                                    /*#__PURE__*/ _jsxs(Grid, {
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        justifyContent: "space-between",
                                                        sx: classes.input,
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Text, {
                                                                sx: classes.label,
                                                                children: "State"
                                                            }),
                                                            /*#__PURE__*/ _jsx(Select, {
                                                                items: states,
                                                                placeholder: "State",
                                                                name: "state",
                                                                value: organizationDetails?.organization?.state ?? '',
                                                                onChange: handleInputChange,
                                                                dataTestId: "state"
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx(Input, {
                                                        placeholder: 'Enter zip',
                                                        label: 'Zip',
                                                        size: InputSize.MD,
                                                        onChange: handleInputChange,
                                                        name: "zipCode",
                                                        textError: displayErrorIfPresent('zipCode', errors),
                                                        isError: hasError('zipCode', errors),
                                                        classes: {
                                                            root: classes.input
                                                        },
                                                        value: organizationDetails?.organization?.zipCode ?? '',
                                                        dataTestId: "zipCode"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs(Grid, {
                                                style: {
                                                    width: '50%'
                                                },
                                                rowGap: 3,
                                                display: "flex",
                                                flexDirection: "column",
                                                sx: classes.input,
                                                children: [
                                                    /*#__PURE__*/ _jsx(Text, {
                                                        sx: classes.label,
                                                        children: "Timezone"
                                                    }),
                                                    /*#__PURE__*/ _jsx(Select, {
                                                        items: convertTimeZones(timezones),
                                                        placeholder: "Timezone",
                                                        onChange: handleInputChange,
                                                        name: 'timeZoneFullName',
                                                        value: organizationDetails?.organization?.timeZoneFullName,
                                                        dataTestId: "timeZoneFullName"
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("fieldset", {
                                style: classes.fieldset,
                                children: [
                                    /*#__PURE__*/ _jsx("legend", {
                                        style: classes.legend,
                                        children: "Authentication"
                                    }),
                                    /*#__PURE__*/ _jsx(Grid, {
                                        sx: classes.grid,
                                        children: /*#__PURE__*/ _jsxs(Grid, {
                                            sx: classes.checkbox,
                                            children: [
                                                /*#__PURE__*/ _jsx(Checkbox, {
                                                    label: 'AWS Cognito Flag',
                                                    size: CheckboxSize.MD,
                                                    isChecked: organizationDetails?.organization?.awsCognitoIndicator,
                                                    onChange: toggleAWSCognitoIndicator,
                                                    name: "cognitoCheckbox",
                                                    dataTestId: "cognitoFlag"
                                                }),
                                                /*#__PURE__*/ _jsx(Checkbox, {
                                                    label: 'Cognito SSO Flag',
                                                    size: CheckboxSize.MD,
                                                    isChecked: organizationDetails?.organization?.ssoIndicator,
                                                    onChange: toggleSSOIndicator,
                                                    name: "ssoCheckbox",
                                                    dataTestId: "ssoFlag"
                                                })
                                            ]
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("fieldset", {
                                style: classes.fieldset,
                                children: [
                                    /*#__PURE__*/ _jsx("legend", {
                                        style: classes.legend,
                                        children: "Contact"
                                    }),
                                    /*#__PURE__*/ _jsxs(Grid, {
                                        sx: classes.grid,
                                        children: [
                                            /*#__PURE__*/ _jsx(Input, {
                                                placeholder: 'Enter name',
                                                label: 'Name',
                                                size: InputSize.MD,
                                                onChange: handleInputChange,
                                                name: "contactName",
                                                textError: displayErrorIfPresent('contactName', errors),
                                                isError: hasError('contactName', errors),
                                                value: organizationDetails?.organization?.contactName ?? '',
                                                dataTestId: "contactName"
                                            }),
                                            /*#__PURE__*/ _jsx(Input, {
                                                placeholder: 'Enter phone number',
                                                label: 'Phone',
                                                size: InputSize.MD,
                                                onChange: handleInputChange,
                                                name: "phone",
                                                textError: displayErrorIfPresent('phone', errors),
                                                isError: hasError('phone', errors),
                                                classes: {
                                                    root: classes.input
                                                },
                                                value: organizationDetails?.organization?.phone ?? '',
                                                dataTestId: "phone"
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
OrganizationForm.displayName = 'OrganizationForm';
export { OrganizationForm };
